/**
 * The register's signature controls.
 *
 * These are the highest-traffic surfaces in the product — the ones a cashier
 * touches thousands of times a day — so they live in the design system with one
 * definition each, not as one-off markup in a screen. Every one of them meets
 * the 44px touch floor.
 */
import { ArrowRight, Minus, Plus } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../cn';
import { Menu, type MenuItem } from './menu';

// ───────────────────────── Pay button ─────────────────────────

/**
 * The one button the whole screen leads to: full width, 76px, one centred word.
 *
 * It used to repeat the total on its right-hand side. That was the same number
 * the cashier is already reading at three times the size two hand-spans above,
 * and carrying it twice made the button a second place to check a figure
 * instead of the place to finish the sale. The amount stays where it is largest
 * — the panel's "К оплате" — and the button says one thing.
 *
 * `total` is still what decides the state: at zero the button goes quiet — not
 * disabled-looking, just inert — because there is nothing to pay for.
 */
export function PayButton({
  total,
  label = 'Оплатить',
  shortcut,
  disabled,
  loading,
  onClick,
  className,
}: {
  total: number;
  label?: string;
  shortcut?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
  className?: string;
}) {
  const empty = total <= 0 || disabled;
  const long = label.length > 12;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={empty || loading}
      aria-busy={loading || undefined}
      className={cn(
        'relative flex h-[76px] w-full items-center justify-center gap-3 rounded-2xl px-16 transition-colors duration-150',
        'active:translate-y-px disabled:pointer-events-none',
        empty
          ? 'bg-rail text-ink-3 shadow-[inset_0_0_0_1px_var(--color-key-edge)]'
          : 'bg-linear-to-b from-primary-top to-primary text-white hover:from-primary hover:to-primary-hover ' +
              'shadow-[var(--shadow-key-fill),0_16px_32px_-14px_rgb(34_85_230/0.6)] active:shadow-[var(--shadow-key-fill-down)]',
        className,
      )}
    >
      <span
        className={cn(
          'min-w-0 truncate font-bold tracking-wide uppercase',
          // "Оплатить" is one word and owns the button; a stand-in like
          // "Продажи остановлены" is a sentence, and setting it at the same
          // size would either clip it or shout a refusal.
          long ? 'text-lg' : 'text-2xl',
        )}
      >
        {label}
      </span>
      {/* The arrow promises the next step, so it only appears when there is
          one: a blocked till is not going anywhere. */}
      {!long && (
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-full',
            empty ? 'bg-line text-ink-3' : 'bg-white/18 text-white',
          )}
          aria-hidden
        >
          <ArrowRight className="size-[22px]" />
        </span>
      )}
      {/* Parked at the edge rather than in the row: the label is centred on the
          button, and a hint at the end would pull it off-centre. */}
      {shortcut && (
        <kbd
          className={cn(
            'absolute top-1/2 right-5 -translate-y-1/2 rounded-sm px-1.5 py-1 font-sans text-2xs font-medium tnum',
            empty ? 'bg-line text-ink-3' : 'bg-white/18 text-white',
          )}
        >
          {shortcut}
        </kbd>
      )}
    </button>
  );
}

// ───────────────────────── Tender tiles ─────────────────────────

/**
 * One payment method in the check's tender grid. Selection is an inset ring so
 * choosing a method never nudges the grid.
 */
export interface TenderTileProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  icon?: ReactNode;
  /** `primary` is one of the two tenders a hand goes to; the rest are quiet. */
  prominence?: 'primary' | 'secondary';
  /**
   * Only for a primary tile. Cash is green because it *is* money changing
   * hands — the one place the payment hue is earned rather than decorative —
   * and card is ink, a neutral weight rather than a second accent colour.
   */
  tone?: 'cash' | 'card';
}

export const TenderTile = forwardRef<HTMLButtonElement, TenderTileProps>(function TenderTile(
  { selected, icon, prominence = 'secondary', tone = 'cash', children, className, type = 'button', ...rest },
  ref,
) {
  const primary = prominence === 'primary';
  return (
    <button
      ref={ref}
      type={type}
      role="radio"
      aria-checked={selected}
      className={cn(
        'flex items-center justify-center gap-2 font-medium transition-colors duration-150',
        'disabled:pointer-events-none disabled:opacity-40',
        primary
          ? 'h-16 rounded-xl px-3 text-md [&_svg]:size-5'
          : 'h-11 rounded-xl px-2 text-sm [&_svg]:size-4',
        primary &&
          tone === 'cash' &&
          'bg-linear-to-b from-success-top to-success text-white hover:brightness-[0.97] ' +
            'shadow-[var(--shadow-key-fill),0_8px_18px_-10px_rgb(14_116_67/0.55)] active:shadow-[var(--shadow-key-fill-down)]',
        primary &&
          tone === 'card' &&
          'bg-linear-to-b from-[#2d2d29] to-ink text-white hover:from-ink-2 hover:to-ink ' +
            'shadow-[inset_0_-3px_0_rgb(0_0_0/0.35),inset_0_1px_0_rgb(255_255_255/0.08),0_8px_18px_-12px_rgb(0_0_0/0.5)] ' +
            'active:shadow-[var(--shadow-key-fill-down)]',
        // A ring, not a fill change: the two primary tiles keep their colour so
        // the eye can find them, and the ring says which one F12 will use.
        primary && selected && 'ring-2 ring-offset-2 ring-offset-surface',
        primary && selected && (tone === 'cash' ? 'ring-success/40' : 'ring-ink/30'),
        !primary &&
          (selected
            ? 'bg-primary-soft text-primary-ink ring-2 ring-primary ring-inset'
            : 'key text-ink-2 hover:bg-sunken hover:text-ink'),
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});

/**
 * A banknote the customer might hand over, with the change it would leave.
 * Tapping one fills the received field; it never submits on its own.
 */
export function CashBin({
  amount,
  hint,
  selected,
  onClick,
}: {
  amount: number;
  hint: string;
  selected?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex h-14 flex-col items-center justify-center rounded-md transition-colors duration-150',
        selected
          ? 'bg-linear-to-b from-primary-top to-primary text-white key-fill'
          : 'key text-ink hover:bg-sunken active:translate-y-px',
      )}
    >
      <span className="text-md font-semibold tnum">{amount.toLocaleString('ru-RU')}</span>
      <span className={cn('text-2xs', selected ? 'text-white/80' : 'text-ink-3')}>{hint}</span>
    </button>
  );
}

// ───────────────────────── Quantity stepper ─────────────────────────

/**
 * Minus / value / plus at 44px each. Used on check lines, on returns and when
 * counting the drawer — it was re-implemented in four places with four
 * different hover colours before it lived here.
 */
export function Stepper({
  value,
  onDecrement,
  onIncrement,
  onValueClick,
  decrementLabel = 'Меньше',
  incrementLabel = 'Больше',
  valueLabel,
  suffix,
  disabledDecrement,
  disabledIncrement,
  className,
}: {
  value: ReactNode;
  onDecrement: () => void;
  onIncrement: () => void;
  /** Makes the value itself a button for typing an exact amount. */
  onValueClick?: () => void;
  decrementLabel?: string;
  incrementLabel?: string;
  valueLabel?: string;
  /** The unit, set quieter than the figure: «1,2 кг» is not «1,2 шт». */
  suffix?: string;
  disabledDecrement?: boolean;
  disabledIncrement?: boolean;
  className?: string;
}) {
  // Raised keys either side of a recessed read-out: the keys are what you
  // press, the number is what you read.
  const side =
    'key flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 transition-colors duration-150 hover:bg-sunken hover:text-ink active:translate-y-px disabled:pointer-events-none disabled:opacity-40';
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <button type="button" aria-label={decrementLabel} onClick={onDecrement} disabled={disabledDecrement} className={side}>
        <Minus className="size-4" />
      </button>
      {onValueClick ? (
        <button
          type="button"
          onClick={onValueClick}
          aria-label={valueLabel}
          className="h-11 min-w-16 rounded-md bg-surface px-3 text-md font-semibold shadow-[inset_0_1px_2px_rgb(27_27_24/0.06)] ring-1 ring-line-strong ring-inset transition-colors duration-150 hover:ring-primary tnum"
        >
          {value}
          {suffix && <span className="ml-1 text-sm font-medium text-ink-3">{suffix}</span>}
        </button>
      ) : (
        <span className="h-11 min-w-16 content-center rounded-md bg-surface px-3 text-center text-md font-semibold shadow-[inset_0_1px_2px_rgb(27_27_24/0.06)] ring-1 ring-line-strong ring-inset tnum">
          {value}
          {suffix && <span className="ml-1 text-sm font-medium text-ink-3">{suffix}</span>}
        </span>
      )}
      <button type="button" aria-label={incrementLabel} onClick={onIncrement} disabled={disabledIncrement} className={side}>
        <Plus className="size-4" />
      </button>
    </div>
  );
}

// ───────────────────────── Category chip ─────────────────────────

/**
 * Category filters select in solid ink, not blue, so the filter row never
 * competes with the highlighted product row or the pay button.
 */
export function Chip({
  selected,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { selected?: boolean }) {
  return (
    <button
      type={type}
      role="tab"
      aria-selected={selected}
      className={cn(
        'h-11 shrink-0 rounded-md px-4 text-base font-medium transition-colors duration-150',
        selected
          ? 'bg-ink text-surface'
          : 'bg-surface text-ink-2 ring-1 ring-line ring-inset hover:bg-sunken hover:text-ink',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

// ───────────────────────── Quick actions ─────────────────────────

export interface QuickActionItem {
  key: string;
  label: string;
  icon: ReactNode;
  shortcut?: string;
  disabled?: boolean;
  /** Pushed to the far end of the strip — for «Ещё», which opens the rest. */
  push?: boolean;
  onClick?: () => void;
  /** Opens a menu of these instead of acting — anchored to the key itself. */
  menu?: (MenuItem | null)[];
}

/**
 * The strip along the bottom of the register: everything a cashier reaches for
 * without leaving the sale. Scrolls horizontally with a fade to show there is
 * more — the only gradient in the system, and it is functional.
 */
export function QuickActionBar({ actions, className }: { actions: QuickActionItem[]; className?: string }) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center gap-1.5 overflow-x-auto',
        '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {actions.map((a) => {
        const key = (
        <button
          key={a.key}
          type="button"
          onClick={a.menu ? undefined : a.onClick}
          disabled={a.disabled}
          className={cn(
            'key group flex h-[52px] shrink-0 items-center gap-1.5 rounded-lg pr-2 pl-1.5 text-sm font-semibold text-ink-2',
            'transition-colors duration-150 hover:text-ink active:translate-y-px disabled:pointer-events-none disabled:opacity-40',
            a.push && 'ml-auto',
          )}
        >
          <span
            className={cn(
              'flex size-[30px] shrink-0 items-center justify-center rounded-md bg-sunken text-ink-2 transition-colors duration-150',
              'group-hover:bg-primary-soft group-hover:text-primary [&_svg]:size-[17px]',
            )}
          >
            {a.icon}
          </span>
          {a.label}
          {a.shortcut && (
            <kbd className="rounded-xs bg-sunken px-1.5 py-0.5 font-sans text-2xs font-semibold text-ink-3 shadow-[inset_0_0_0_1px_var(--color-key-edge)] tnum">
              {a.shortcut}
            </kbd>
          )}
        </button>
        );
        return a.menu ? <Menu key={a.key} trigger={key} items={a.menu} /> : key;
      })}
    </div>
  );
}
